import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  Loader2,
  Search,
  Smartphone,
  ToggleLeft,
  ToggleRight,
  Wallet,
  Building2,
  X,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import useAuthAccess from "../../hooks/useAuthAccess";
import { db } from "../../firebase";
import { fetchAdminPayments } from "../../services/api";
import AdminLayout from "../layout/AdminLayout";
import PaymentDetailModal from "../../Components/PaymentDetailModal";
import { formatAmountPaise, formatMembershipDate, parseMembershipDate } from "../../utils/membership";

function methodIcon(method) {
  if (method === "upi") return Smartphone;
  if (method === "wallet") return Wallet;
  if (method === "netbanking") return Building2;
  return CreditCard;
}

const DEFAULT_PLANS = [
  {
    id: "premium_6months",
    name: "Karanki Premium (6 Months)",
    durationMonths: 6,
    baseAmount: 499900,
    originalAmount: 799900,
    gstPercent: 0,
    gstAmount: 0,
    finalAmount: 499900,
    currency: "INR",
    active: true,
    badge: "",
    sortOrder: 1,
  },
  {
    id: "premium_annual",
    name: "Karanki Premium (1 Year)",
    durationMonths: 12,
    baseAmount: 699900,
    originalAmount: 1199900,
    gstPercent: 0,
    gstAmount: 0,
    finalAmount: 699900,
    currency: "INR",
    active: true,
    badge: "Most Popular",
    sortOrder: 2,
  },
];

const DISABLE_REASONS = [
  "Payment gateway maintenance",
  "Plans are being updated",
  "Temporary technical issue",
  "Payments paused by admin",
];

const Payments = () => {
  const navigate = useNavigate();
  const { user, role, loading: authLoading } = useAuthAccess();
  const [search, setSearch] = useState("");
  const [methodFilter, setMethodFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [plans, setPlans] = useState(DEFAULT_PLANS);
  const [savingPlanId, setSavingPlanId] = useState("");
  const [disableTarget, setDisableTarget] = useState(null);
  const [disableReason, setDisableReason] = useState(DISABLE_REASONS[0]);
  const [customDisableReason, setCustomDisableReason] = useState("");

  const adminPaymentsQuery = useQuery({
    queryKey: ["adminPayments", user?.uid],
    queryFn: async () => {
      if (!user) {
        throw new Error("Please log in as admin.");
      }

      const idToken = await user.getIdToken();
      const result = await fetchAdminPayments(idToken);
      return result.payments || [];
    },
    enabled: !!user && role === "admin",
    staleTime: 300000,
    cacheTime: 600000,
    retry: false,
  });

  const plansQuery = useQuery({
    queryKey: ["adminPaymentPlans"],
    queryFn: async () => {
      const snapshot = await getDocs(
        query(collection(db, "membershipPlans"), orderBy("sortOrder", "asc"))
      );
      return snapshot.docs.map((planDoc) => ({ id: planDoc.id, ...planDoc.data() }));
    },
    enabled: !!user && role === "admin",
    staleTime: 300000,
    cacheTime: 600000,
    retry: 1,
  });

  useEffect(() => {
    if (!plansQuery.data) return;
    if (!plansQuery.data.length) {
      setPlans(DEFAULT_PLANS);
      return;
    }

    setPlans(
      DEFAULT_PLANS.map((fallback) => ({
        ...fallback,
        ...(plansQuery.data.find((plan) => plan.id === fallback.id) || {}),
      }))
    );
  }, [plansQuery.data]);

  const writePlanActive = async (plan, active, reason = "") => {
    const fallback = DEFAULT_PLANS.find((item) => item.id === plan.id) || {};
    const disablePayload = active
      ? {
          disabledReason: "",
          disabledReasonLabel: "",
          disabledAt: null,
        }
      : {
          disabledReason: reason,
          disabledReasonLabel: reason,
          disabledAt: serverTimestamp(),
        };

    await setDoc(
      doc(db, "membershipPlans", plan.id),
      {
        ...fallback,
        ...plan,
        active,
        ...disablePayload,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  };

  const togglePlan = async (plan) => {
    const nextActive = plan.active === false;
    if (!nextActive) {
      setDisableTarget({ type: "plan", plan });
      setDisableReason(plan.disabledReason || DISABLE_REASONS[0]);
      setCustomDisableReason("");
      return;
    }

    try {
      setSavingPlanId(plan.id);
      await writePlanActive(plan, nextActive);
      setPlans((current) =>
        current.map((item) =>
          item.id === plan.id
            ? { ...item, active: nextActive, disabledReason: "", disabledReasonLabel: "" }
            : item
        )
      );
      toast.success("Plan enabled");
      plansQuery.refetch();
    } catch (planError) {
      toast.error(planError.message || "Could not update plan.");
    } finally {
      setSavingPlanId("");
    }
  };

  const setAllPayments = async (active, reason = "") => {
    try {
      setSavingPlanId("all");
      await Promise.all(plans.map((plan) => writePlanActive(plan, active, reason)));
      setPlans((current) =>
        current.map((plan) => ({
          ...plan,
          active,
          disabledReason: active ? "" : reason,
          disabledReasonLabel: active ? "" : reason,
        }))
      );
      toast.success(active ? "Payments enabled" : "Payments disabled");
      plansQuery.refetch();
    } catch (planError) {
      toast.error(planError.message || "Could not update payment status.");
    } finally {
      setSavingPlanId("");
    }
  };

  const openDisableAll = () => {
    setDisableTarget({ type: "all" });
    setDisableReason(DISABLE_REASONS[0]);
    setCustomDisableReason("");
  };

  const closeDisableReason = () => {
    if (savingPlanId) return;
    setDisableTarget(null);
    setCustomDisableReason("");
  };

  const confirmDisable = async () => {
    if (!disableTarget) return;
    const reason = (customDisableReason.trim() || disableReason).trim();
    if (!reason) {
      toast.error("Please select or enter a reason.");
      return;
    }

    if (disableTarget.type === "all") {
      await setAllPayments(false, reason);
    } else if (disableTarget.plan) {
      const plan = disableTarget.plan;
      try {
        setSavingPlanId(plan.id);
        await writePlanActive(plan, false, reason);
        setPlans((current) =>
          current.map((item) =>
            item.id === plan.id
              ? { ...item, active: false, disabledReason: reason, disabledReasonLabel: reason }
              : item
          )
        );
        toast.success("Plan disabled");
        plansQuery.refetch();
      } catch (planError) {
        toast.error(planError.message || "Could not update plan.");
      } finally {
        setSavingPlanId("");
      }
    }

    setDisableTarget(null);
    setCustomDisableReason("");
  };

  const payments = useMemo(() => adminPaymentsQuery.data || [], [adminPaymentsQuery.data]);
  const activePlanCount = plans.filter((plan) => plan.active !== false).length;
  const paymentsEnabled = activePlanCount > 0;
  const loading = authLoading || adminPaymentsQuery.isLoading;
  const error = !authLoading && role !== "admin"
    ? "Please log in as admin."
    : adminPaymentsQuery.error?.message || "";

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return payments.filter((payment) => {
      if (methodFilter !== "all" && payment.method !== methodFilter) return false;
      if (!term) return true;

      const haystack = [
        payment.memberName,
        payment.memberPhone,
        payment.memberEmail,
        payment.paymentId,
        payment.orderId,
        payment.methodLabel,
        payment.profileId,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [payments, search, methodFilter]);

  const stats = useMemo(() => {
    const totalRevenue = payments.reduce(
      (sum, item) => sum + (Number(item.amount) || 0),
      0
    );
    const byMethod = payments.reduce((acc, item) => {
      const key = item.methodLabel || item.method || "Other";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return {
      count: payments.length,
      totalRevenue,
      byMethod,
    };
  }, [payments]);

  const methodFilters = useMemo(() => {
    const methods = new Set(payments.map((p) => p.method).filter(Boolean));
    return ["all", ...methods];
  }, [payments]);

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#c90243]">Payments</h1>
        <p className="mt-1 text-[#666]">
          Premium membership transactions — tap any row for full receipt and payment details
        </p>
      </div>

      {error && (
        <p className="mb-4 rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
      )}

      {role === "admin" && (
        <div className="mb-6 overflow-hidden rounded-[28px] border border-[#E7E0DC] bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-[#E7E0DC] bg-[#FAF7F2] px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-[#777]">Payment controls</p>
              <h2 className="mt-1 text-xl font-bold text-[#222]">
                {paymentsEnabled ? "Premium payments are enabled" : "Premium payments are disabled"}
              </h2>
              <p className="mt-1 text-sm text-[#666]">
                Disabling all plans hides pay buttons and plans from users.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setAllPayments(true)}
                disabled={savingPlanId === "all" || paymentsEnabled}
                className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {savingPlanId === "all" ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                Enable payments
              </button>
              <button
                type="button"
                onClick={openDisableAll}
                disabled={savingPlanId === "all" || !paymentsEnabled}
                className="inline-flex items-center gap-2 rounded-2xl bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {savingPlanId === "all" ? <Loader2 size={16} className="animate-spin" /> : <AlertTriangle size={16} />}
                Disable payments
              </button>
            </div>
          </div>

          <div className="grid gap-3 p-5 lg:grid-cols-2">
            {plansQuery.isLoading ? (
              <div className="col-span-full flex justify-center py-6">
                <Loader2 className="animate-spin text-[#c90243]" size={24} />
              </div>
            ) : (
              plans.map((plan) => {
                const active = plan.active !== false;
                const busy = savingPlanId === plan.id || savingPlanId === "all";
                return (
                  <div key={plan.id} className="rounded-[22px] border border-[#E7E0DC] bg-white px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-[#222]">{plan.name || plan.id}</p>
                        <p className="mt-0.5 text-xs text-[#777]">
                          {formatAmountPaise(plan.finalAmount, plan.currency)} · {plan.durationMonths || "-"} months
                        </p>
                        {!active && plan.disabledReason && (
                          <p className="mt-1 text-xs font-semibold text-amber-700">
                            Reason: {plan.disabledReason}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => togglePlan(plan)}
                        disabled={busy}
                        className={`inline-flex shrink-0 items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                          active
                            ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                            : "bg-sand-100 text-[#777] hover:bg-sand-200"
                        }`}
                      >
                        {busy ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : active ? (
                          <ToggleRight size={16} />
                        ) : (
                          <ToggleLeft size={16} />
                        )}
                        {active ? "Enabled" : "Disabled"}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-[24px] border border-[#E7E0DC] bg-white p-5 shadow-sm">
          <p className="text-sm text-[#777]">Total transactions</p>
          <p className="mt-2 text-3xl font-bold text-[#222]">{stats.count}</p>
        </div>
        <div className="rounded-[24px] border border-[#E7E0DC] bg-white p-5 shadow-sm">
          <p className="text-sm text-[#777]">Total revenue</p>
          <p className="mt-2 text-3xl font-bold text-[#c90243]">
            {formatAmountPaise(stats.totalRevenue)}
          </p>
        </div>
        <div className="rounded-[24px] border border-[#E7E0DC] bg-white p-5 shadow-sm sm:col-span-2">
          <p className="mb-2 text-sm text-[#777]">By payment mode</p>
          <div className="flex flex-wrap gap-2">
            {Object.keys(stats.byMethod).length === 0 ? (
              <span className="text-sm text-gray-500">No payments yet</span>
            ) : (
              Object.entries(stats.byMethod).map(([label, count]) => (
                <span
                  key={label}
                  className="rounded-full bg-[#FAF2F5] px-3 py-1 text-xs font-semibold text-[#c90243]"
                >
                  {label}: {count}
                </span>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999]" size={18} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone, payment ID, order ID…"
            className="w-full rounded-2xl border border-[#E7E0DC] bg-white py-3 pl-11 pr-4 outline-none focus:border-[#c90243]"
          />
        </div>
        <select
          value={methodFilter}
          onChange={(e) => setMethodFilter(e.target.value)}
          className="rounded-2xl border border-[#E7E0DC] bg-white px-4 py-3 text-sm font-medium outline-none focus:border-[#c90243]"
        >
          {methodFilters.map((m) => (
            <option key={m} value={m}>
              {m === "all" ? "All payment modes" : m}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-hidden rounded-[28px] border border-[#E7E0DC] bg-white shadow-sm">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin text-[#c90243]" size={36} />
          </div>
        ) : filtered.length === 0 ? (
          <p className="px-6 py-12 text-center text-sm text-gray-500">
            No payment transactions found. New premium payments will appear here automatically.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-[#E7E0DC] bg-[#FAF7F2] text-xs uppercase tracking-wide text-[#777]">
                <tr>
                  <th className="px-5 py-4">Member</th>
                  <th className="px-5 py-4">Amount</th>
                  <th className="px-5 py-4">Mode</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4">Valid until</th>
                  <th className="px-5 py-4">Env</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((payment) => {
                  const Icon = methodIcon(payment.method);
                  return (
                    <tr
                      key={payment.id}
                      onClick={() => setSelected(payment)}
                      className="cursor-pointer border-b border-[#F1E8E2] transition hover:bg-white"
                    >
                      <td className="px-5 py-4">
                        <p className="font-semibold text-[#222]">{payment.memberName || "—"}</p>
                        <p className="text-xs text-gray-500">{payment.memberPhone || "—"}</p>
                      </td>
                      <td className="px-5 py-4 font-semibold text-[#c90243]">
                        {formatAmountPaise(payment.amount, payment.currency)}
                      </td>
                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FAF2F5] px-2.5 py-1 text-xs font-semibold text-[#c90243]">
                          <Icon size={14} />
                          {payment.methodLabel || payment.method || "—"}
                        </span>
                        {payment.methodDetail && (
                          <p className="mt-1 text-xs text-gray-500">{payment.methodDetail}</p>
                        )}
                      </td>
                      <td className="px-5 py-4 capitalize">{payment.status || "—"}</td>
                      <td className="px-5 py-4">
                        {payment.status === "failed"
                          ? formatMembershipDate(parseMembershipDate(payment.failedAt || payment.createdAt))
                          : formatMembershipDate(parseMembershipDate(payment.paidAt))}
                      </td>
                      <td className="px-5 py-4">
                        {formatMembershipDate(parseMembershipDate(payment.membershipExpiresAt))}
                      </td>
                      <td className="px-5 py-4 uppercase text-xs text-gray-500">
                        {payment.environment || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {disableTarget && (
        <div
          className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 px-4 backdrop-blur-sm sm:items-center"
          onClick={closeDisableReason}
        >
          <div
            className="w-full max-w-lg rounded-t-[28px] bg-white p-5 shadow-2xl sm:rounded-[28px]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-[#c90243]">Disable reason</p>
                <h2 className="mt-1 text-xl font-bold text-[#222]">
                  {disableTarget.type === "all" ? "Disable all payments" : "Disable this plan"}
                </h2>
                <p className="mt-1 text-sm text-[#666]">
                  This reason will be shown on the user membership page.
                </p>
              </div>
              <button
                type="button"
                onClick={closeDisableReason}
                className="rounded-xl p-2 text-[#777] transition hover:bg-[#FAF2F5] hover:text-[#c90243]"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mt-5 space-y-3">
              {DISABLE_REASONS.map((reason) => (
                <label
                  key={reason}
                  className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-sm font-semibold transition ${
                    disableReason === reason
                      ? "border-[#c90243] bg-[#FAF2F5] text-[#c90243]"
                      : "border-[#E7E0DC] text-[#444] hover:border-[#c90243]"
                  }`}
                >
                  <input
                    type="radio"
                    name="disableReason"
                    checked={disableReason === reason}
                    onChange={() => setDisableReason(reason)}
                    className="h-4 w-4 accent-[#c90243]"
                  />
                  {reason}
                </label>
              ))}

              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-[#777]">
                  Custom reason
                </label>
                <textarea
                  value={customDisableReason}
                  onChange={(event) => setCustomDisableReason(event.target.value)}
                  placeholder="Optional custom message for users"
                  rows={3}
                  className="w-full resize-none rounded-2xl border border-[#E7E0DC] px-4 py-3 text-sm outline-none transition focus:border-[#c90243]"
                />
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={closeDisableReason}
                className="rounded-2xl border border-[#E7E0DC] bg-white py-3 text-sm font-semibold text-[#444] transition hover:bg-[#FAF7F2]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDisable}
                disabled={Boolean(savingPlanId)}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-rose-600 py-3 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {savingPlanId ? <Loader2 size={16} className="animate-spin" /> : <AlertTriangle size={16} />}
                Disable
              </button>
            </div>
          </div>
        </div>
      )}

      <PaymentDetailModal
        payment={selected}
        onClose={() => setSelected(null)}
        showAdminFields
        footerAction={
          selected ? (
            <button
              type="button"
              onClick={() => {
                const profileId = selected.profileId;
                setSelected(null);
                navigate(`/admin/users/${profileId}`);
              }}
              className="w-full rounded-2xl bg-[#c90243] py-3 text-sm font-semibold text-white"
            >
              View member profile
            </button>
          ) : null
        }
      />
    </AdminLayout>
  );
};

export default Payments;
